import { Component } from "react"

/* Modal */
import Modal from "react-modal"

/* Redux */
import { connect } from "react-redux"

/* Translate */
import { t } from "../../translates/translate"

/* REST API */
import { detail } from "../../api/Rsg"

/* Helpers */
import { utils } from "../../helpers"

/* Icons */
import { CloseIcon, InfoIcon, SearchIcon, CopyIcon, DownloadIcon, ReportIcon, GameIcon, ChartIcon, CalendarIcon, ClockIcon, HashIcon, SendIcon, StudioIcon } from "./RsgIcons"

/* Utils */
import { formatAmount, formatSigned, formatDate, formatPeriod, downloadReport } from "./RsgUtils"

/* Sweet alert */
import Swal from "../../helpers/alert"


/* EMPTY MODAL STATE */
const EMPTY = {
    id: 0,
    status: "loading",
    report: null,
    games: [],
    search: "",
    waiting: false,
}


/* Widget Rsg Report */
class RsgReport extends Component {

    constructor() {
        super()
        this.state = { visible: false, ...EMPTY }
    }

    /* Open Action */
    open = ({ id = 0 }) => {

        this.setState({ visible: true, ...EMPTY, id: parseInt(id) ? parseInt(id) : 0 }, () => {
            this.load()
        })
    }

    /* Close Action */
    close = () => {
        this.setState({ visible: false, ...EMPTY })
    }

    /* Load Action — the games of a report never travel with the list */
    load = () => {

        /* Fields */
        const { id } = this.state

        /* Set Status */
        this.setState({ status: "loading" })

        /* Send to REST API */
        detail(id).then((response) => {
            if (response.status === 200) {
                this.setState({ status: "ok", report: response.data.report, games: response.data.games })
            }
            else {
                this.setState({ status: "error" })
                Swal.fire(utils.notification(this.message(response), "error"))
                this.close()
            }
        }).catch(() => {
            this.setState({ status: "error" })
            Swal.fire(utils.notification(t("swal.generic"), "error"))
            this.close()
        })

    }

    /* Read The Error Message Of A Response */
    message = (response) => {

        if (response && response.data && response.data.message) {
            return response.data.message
        }

        if (response && response.status === 498) {
            return t("swal.invalidKey")
        }

        if (response && (response.status === 401 || response.status === 403)) {
            return t("swal.noAccess")
        }

        return t("swal.generic")
    }

    /* Games Of The Search — a monthly report carries every table of every studio */
    getGames = () => {

        /* Fields */
        const { games, search } = this.state
        const value = search.trim().toLowerCase()

        /* Check Search */
        if (value === "") {
            return games
        }

        /* Send Result */
        return games.filter((item) => {

            const name = String(utils.checkValue(item.tableName, "")).toLowerCase()
            const gameID = String(utils.checkValue(item.gameID, "")).toLowerCase()
            const license = String(utils.checkValue(item.licenseNumber, "")).toLowerCase()

            return name.includes(value) || gameID.includes(value) || license.includes(value)
        })
    }

    /* Copy Action — the hash goes to the clipboard */
    copy = (value) => {

        /* Check Value */
        if (!value) {
            return
        }

        /* Check Clipboard — an insecure origin has none */
        if (!navigator.clipboard) {
            Swal.fire(utils.notification(t("rsg.copyFailed"), "error"))
            return
        }

        navigator.clipboard.writeText(value).then(() => {
            Swal.fire(utils.notification(t("rsg.hashCopied"), "success"))
        }).catch(() => {
            Swal.fire(utils.notification(t("rsg.copyFailed"), "error"))
        })

    }

    /* Download Action */
    download = () => {

        /* Fields */
        const { report, waiting } = this.state

        /* Check Waiting */
        if (waiting) {
            return
        }

        /* Start Waiting */
        this.setState({ waiting: true })

        downloadReport(report).finally(() => {
            this.setState({ waiting: false })
        })

    }

    /* Draw Totals — the money of the whole report */
    _totals = () => {

        /* Fields */
        const { report } = this.state

        return (
            <div className="rsg--section">

                <div className="rsg--section-head">
                    <div className="rsg--section-title">
                        <ChartIcon />
                        {t("rsg.totals")}
                    </div>
                </div>

                <div className="rsg--stats">

                    <div className="rsg--stat">
                        <div className="rsg--stat-title">GGR USD</div>
                        <div className={`rsg--stat-value ${parseFloat(report.ggrUSD) >= 0 ? "in" : "out"}`}>{formatSigned(report.ggrUSD)} $</div>
                    </div>

                    <div className="rsg--stat">
                        <div className="rsg--stat-title">GGR GEL</div>
                        <div className={`rsg--stat-value ${parseFloat(report.ggr) >= 0 ? "in" : "out"}`}>{formatSigned(report.ggr)} ₾</div>
                    </div>

                    <div className="rsg--stat">
                        <div className="rsg--stat-title">{t("rsg.amountIn")} GEL</div>
                        <div className="rsg--stat-value">{formatAmount(report.amtIn)} ₾</div>
                    </div>

                    <div className="rsg--stat">
                        <div className="rsg--stat-title">{t("rsg.amountOut")} GEL</div>
                        <div className="rsg--stat-value">{formatAmount(report.amtOut)} ₾</div>
                    </div>

                </div>

            </div>
        )
    }

    /* Draw Information — everything the regulator received together with the file */
    _information = () => {

        /* Fields */
        const { report } = this.state

        return (
            <div className="rsg--section">

                <div className="rsg--section-head">
                    <div className="rsg--section-title">
                        <ReportIcon size={13} />
                        {t("rsg.information")}
                    </div>
                </div>

                <div className="rsg--info">

                    <div className="rsg--info-item">
                        <CalendarIcon />
                        <div className="rsg--info-text">
                            <div className="rsg--info-title">{t("rsg.reportDate")}</div>
                            <div className="rsg--info-value">{formatPeriod(report.reportDate, report.reportType)}</div>
                        </div>
                    </div>

                    <div className="rsg--info-item">
                        <ClockIcon />
                        <div className="rsg--info-text">
                            <div className="rsg--info-title">{t("rsg.sentAt")}</div>
                            <div className="rsg--info-value">{formatDate(report.uploadDate || report.createdAt)}</div>
                        </div>
                    </div>

                    <div className="rsg--info-item">
                        <SendIcon />
                        <div className="rsg--info-text">
                            <div className="rsg--info-title">{t("rsg.status")}</div>
                            <div className={`rsg--info-value ${parseInt(report.uploaded) === 1 ? "green" : "red"}`}>
                                {parseInt(report.uploaded) === 1 ? t("rsg.uploaded") : t("rsg.notUploaded")}
                            </div>
                        </div>
                    </div>

                    <div className="rsg--info-item">
                        <GameIcon />
                        <div className="rsg--info-text">
                            <div className="rsg--info-title">{t("rsg.games")}</div>
                            <div className="rsg--info-value">{report.gamecount}</div>
                        </div>
                    </div>

                </div>

                <div className="rsg--hash">

                    <div className="rsg--hash-text">
                        <HashIcon />
                        <span>{utils.checkValue(report.hash, t("rsg.noHash"))}</span>
                    </div>

                    {report.hash
                        ? <div onClick={() => this.copy(report.hash)} className="rsg--hash-copy" title={t("rsg.copyHash")}>
                            <CopyIcon />
                        </div>
                        : null
                    }

                </div>

                {!report.fileExists &&
                    <div className="rsg--hint red">
                        <InfoIcon />
                        <span>{t("rsg.fileMissingHint")}</span>
                    </div>
                }

                {/* The report was regenerated after it had been sent — the download gives the newer file */}
                {report.fileExists && report.fileRegenerated &&
                    <div className="rsg--hint">
                        <InfoIcon />
                        <span>{t("rsg.fileRegeneratedHint")} {report.file}</span>
                    </div>
                }

            </div>
        )
    }

    /* Draw Games — one line for every game the regulator received */
    _games = () => {

        /* Fields */
        const { games, search } = this.state
        const list = this.getGames()

        return (
            <div className="rsg--section">

                <div className="rsg--section-head">

                    <div className="rsg--section-title">
                        <GameIcon />
                        {t("rsg.games")}
                        <span className={`rsg--counter ${games.length === 0 ? "empty" : ""}`}>{games.length}</span>
                    </div>

                    {games.length > 4 &&
                        <div className="rsg--inner-search">
                            <SearchIcon size={14} />
                            <input value={search} onChange={(event) => this.setState({ search: event.target.value })} placeholder={t("rsg.searchByGame")} />
                            {search !== "" &&
                                <div onClick={() => this.setState({ search: "" })} className="rsg--inner-search-clear">
                                    <CloseIcon size={11} />
                                </div>
                            }
                        </div>
                    }

                </div>

                {list.length === 0 &&
                    <div className="rsg--chips-empty">{search !== "" ? t("rsg.nothingFound") : t("rsg.empty")}</div>
                }

                {list.map((item, index) => {

                    /* GGR — the money that came in minus the money that went out */
                    const ggrUSD = (parseFloat(item.amtToGameUSD) || 0) - (parseFloat(item.amtFromGameUSD) || 0)
                    const ggrGEL = (parseFloat(item.amtToGameGEL) || 0) - (parseFloat(item.amtFromGameGEL) || 0)

                    return (
                        <div className="rsg--game" key={`${index}`}>

                            <div className="rsg--game-head">

                                <div className="rsg--game-icon"><GameIcon size={14} /></div>

                                <div className="rsg--game-text">
                                    <div className="rsg--game-name">{utils.checkValue(item.tableName, item.gameID)}</div>
                                    <div className="rsg--game-id">
                                        <span>{item.gameID}</span>
                                        {item.licenseNumber ? <span>{item.licenseNumber}</span> : null}
                                    </div>
                                </div>

                                {item.studioName
                                    ? <div className="rsg--game-studio">
                                        <StudioIcon size={12} />
                                        <span>{item.studioName}</span>
                                    </div>
                                    : null
                                }

                            </div>

                            <div className="rsg--game-amounts">

                                <div className="rsg--game-amount">
                                    <div className="rsg--game-amount-title">USD</div>
                                    <div className="rsg--game-amount-value">
                                        <span className="in">+{formatAmount(item.amtToGameUSD)}</span>
                                        <span className="out">-{formatAmount(item.amtFromGameUSD)}</span>
                                    </div>
                                </div>

                                <div className="rsg--game-amount">
                                    <div className="rsg--game-amount-title">GEL</div>
                                    <div className="rsg--game-amount-value">
                                        <span className="in">+{formatAmount(item.amtToGameGEL)}</span>
                                        <span className="out">-{formatAmount(item.amtFromGameGEL)}</span>
                                    </div>
                                </div>

                                <div className="rsg--game-amount">
                                    <div className="rsg--game-amount-title">{t("rsg.ggr")}</div>
                                    <div className="rsg--game-amount-value">
                                        <span className={ggrUSD >= 0 ? "in" : "out"}>{formatSigned(ggrUSD)} $</span>
                                        <span className={ggrGEL >= 0 ? "in" : "out"}>{formatSigned(ggrGEL)} ₾</span>
                                    </div>
                                </div>

                            </div>

                        </div>
                    )
                })}

            </div>
        )
    }

    /* Draw Body */
    _body = () => {

        /* Fields */
        const { status } = this.state

        /* Check Status */
        if (status !== "ok") {
            return <div className="rsg--modal-loading"><div className="rsg--loading" /></div>
        }

        return (
            <div className="rsg--modal-body">

                {/* Draw Totals */}
                {this._totals()}

                {/* Draw Information */}
                {this._information()}

                {/* Draw Games */}
                {this._games()}

            </div>
        )
    }

    /* Draw Footer */
    _footer = () => {

        /* Fields */
        const { status, report, waiting } = this.state

        /* Check Status */
        if (status !== "ok") {
            return null
        }

        return (
            <div className={`rsg--modal-footer ${report.fileExists ? "space" : ""}`}>

                {report.fileExists &&
                    (waiting
                        ? <div className="rsg--button primary"><div className="rsg--loading small" /></div>
                        : <div onClick={() => this.download()} className="rsg--button primary">
                            <DownloadIcon />
                            <span>{t("rsg.fileDownload")}</span>
                        </div>
                    )
                }

                <div onClick={() => this.close()} className="rsg--button ghost">{t("common.close")}</div>

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { visible, report } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={() => this.close()}
                className="rsg--modal"
                overlayClassName="rsg--overlay"
                closeTimeoutMS={200}
            >

                <div className="rsg--modal-header">

                    <div className="rsg--modal-head-text">
                        <h2>{report ? report.fileName : t("rsg.detailTitle")}</h2>
                        <p>{report ? `${report.reportType} · ${formatPeriod(report.reportDate, report.reportType)}` : t("rsg.detailSubtitle")}</p>
                    </div>

                    <div onClick={() => this.close()} className="rsg--modal-close">
                        <CloseIcon />
                    </div>

                </div>

                {/* Draw Body */}
                {this._body()}

                {/* Draw Footer */}
                {this._footer()}

            </Modal>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(RsgReport)
